/**
 * 職等資料 — **出廠回退值**（2026-08-20 起）
 *
 * ⚠️ 這份常數不再是系統的真相來源。實際生效的是後端依「夥伴所屬部門」解析出的地圖
 * （GET /api/level-maps/mine，見 contexts/LevelMapContext）。
 * 這裡只在 API 尚未回應或載入失敗時頂著，避免 LevelBadge 之類的元件閃爍或開天窗。
 *
 * 要調整正式內容請到後台「夥伴地圖管理」（/admin/level-maps），不要改這個檔。
 * 內容需與 backend/src/constants/defaultLevelMap.ts 保持一致。
 */

// 職等資料配置
export interface LevelData {
  level: number
  name: string
  description: string
  timeRequirement: string
  requirements: string[]
  benefits: string[]
  hourlyRate: number
  colorClass: string
  gradientClass: string
  borderClass: string
  isSpecial?: boolean
  isLegendary?: boolean
  isPartner?: boolean
}

// 完整的職等資料配置（基於設計稿）
export const levelData: LevelData[] = [
  {
    level: 0,
    name: '見習夥伴',
    description: '1個月試用期間',
    timeRequirement: '新進入職',
    requirements: ['完成基礎培訓課程', '熟悉工作流程和規範', '展現積極學習態度'],
    benefits: ['試用期間不提供福利'],
    hourlyRate: 200,
    colorClass: 'bg-gray-500',
    gradientClass: 'from-gray-400 to-gray-600',
    borderClass: 'border-gray-600'
  },
  {
    level: 1,
    name: '正式夥伴',
    description: '通過試用期',
    timeRequirement: 'LV0經過1個月後可申請升等',
    requirements: ['通過試用期考核', '能獨立完成基本任務', '具備良好溝通能力'],
    benefits: ['咖啡補助NT$ 100/月', '績效達標獎金NT$ 100/月'],
    hourlyRate: 210,
    colorClass: 'bg-amber-800',
    gradientClass: 'from-amber-700 to-amber-900',
    borderClass: 'border-amber-900'
  },
  {
    level: 2,
    name: '專業夥伴',
    description: 'LV1後2個月',
    timeRequirement: 'LV1經過2個月後可申請升等',
    requirements: ['具備問題解決能力', '可協助新進人員', '展現工作熱忱', '超乎預期交付質量'],
    benefits: ['咖啡補助NT$ 150/月', '生日禮金NT$ 150/年', '績效達標獎金NT$ 150/月'],
    hourlyRate: 220,
    colorClass: 'bg-amber-600',
    gradientClass: 'from-amber-500 to-amber-700',
    borderClass: 'border-amber-700'
  },
  {
    level: 3,
    name: '高級專業夥伴',
    description: 'LV2後3個月',
    timeRequirement: 'LV2經過3個月後可申請升等',
    requirements: ['可獨立處理複雜任務', '具備培訓能力', '主動發現改善機會', '主動為公司規劃創造營收'],
    benefits: ['咖啡補助NT$ 200/月', '生日禮金NT$ 200/年', '健身補助NT$ 200/月', '績效達標獎金NT$ 200/月'],
    hourlyRate: 240,
    colorClass: 'bg-amber-500',
    gradientClass: 'from-amber-400 to-amber-600',
    borderClass: 'border-amber-600'
  },
  {
    level: 4,
    name: '資深專業夥伴',
    description: 'LV3後3個月',
    timeRequirement: 'LV3經過3個月後可申請升等',
    requirements: ['帶領團隊執行專案', '培訓團隊成員', '制定工作流程標準', '參與策略規劃', '提升團隊整體效能'],
    benefits: ['咖啡補助NT$ 250/月', '生日禮金NT$ 250/年', '健身補助NT$ 250/月', '績效達標獎金NT$ 250/月'],
    hourlyRate: 250,
    colorClass: 'bg-emerald-600',
    gradientClass: 'from-emerald-500 to-emerald-700',
    borderClass: 'border-emerald-700'
  },
  {
    level: 5,
    name: '組長級夥伴',
    description: 'LV4後3個月',
    timeRequirement: 'LV4經過3個月後可申請升等',
    requirements: ['跨團隊資源整合能力', '建立數據分析體系', '優化營運效率系統', '提出創新解決方案', '培育團隊核心成員'],
    benefits: ['咖啡補助NT$ 300/月', '生日禮金NT$ 300/年', '健身補助NT$ 300/月', '績效達標獎金NT$ 300/月'],
    hourlyRate: 260,
    colorClass: 'bg-blue-600',
    gradientClass: 'from-blue-500 to-blue-700',
    borderClass: 'border-blue-700'
  },
  {
    level: 6,
    name: '專業組長級夥伴',
    description: 'LV5後3個月',
    timeRequirement: 'LV5經過3個月後可申請升等',
    requirements: ['規劃部門成長策略', '建立可擴展團隊架構', '開發創新商業模式', '建立跨部門合作機制', '打造高效能團隊文化'],
    benefits: ['咖啡補助NT$ 350/月', '生日禮金NT$ 350/年', '健身補助NT$ 350/月', '績效達標獎金NT$ 350/月'],
    hourlyRate: 280,
    colorClass: 'bg-purple-600',
    gradientClass: 'from-purple-500 to-purple-700',
    borderClass: 'border-purple-700',
    isSpecial: true
  },
  {
    level: 7,
    name: '資深組長級夥伴',
    description: 'LV6後3個月',
    timeRequirement: 'LV6經過3個月後可申請升等',
    requirements: ['制定公司發展願景', '建立企業核心競爭力', '打造創新營運模式', '建構組織成長系統', '整合產業生態資源'],
    benefits: ['咖啡補助NT$ 400/月', '生日禮金NT$ 400/年', '健身補助NT$ 400/月', '績效達標獎金NT$ 400/月'],
    hourlyRate: 300,
    colorClass: 'bg-pink-600',
    gradientClass: 'from-pink-500 to-pink-700',
    borderClass: 'border-pink-700',
    isSpecial: true
  },
  {
    level: 8,
    name: '(神秘：未來解鎖)',
    description: '待訂',
    timeRequirement: 'LV7經過4個月後可申請升等',
    requirements: ['建立多元事業體系', '培養接班人梯隊', '開拓國際市場', '建立企業文化體系', '實現跨界合作創新'],
    benefits: ['咖啡補助NT$ 500/月', '生日禮金NT$ 500/年', '健身補助NT$ 500/月', '績效達標獎金NT$ 500/月', '年度獎勵旅遊'],
    hourlyRate: 350,
    colorClass: 'bg-red-600',
    gradientClass: 'from-red-500 to-red-700',
    borderClass: 'border-red-700',
    isLegendary: true
  },
  {
    level: 9,
    name: '(神秘：未來解鎖)',
    description: '待訂',
    timeRequirement: 'LV8經過6個月後可申請升等',
    requirements: ['打造產業生態圈', '建立企業永續發展', '培養行業領導地位', '實現社會影響力', '建構未來願景藍圖'],
    benefits: ['咖啡補助NT$ 600/月', '生日禮金NT$ 600/年', '健身補助NT$ 600/月', '績效達標獎金NT$ 600/月', '年度獎勵旅遊', '股權激勵計劃'],
    hourlyRate: 400,
    colorClass: 'bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 to-purple-500',
    gradientClass: 'from-red-400 via-yellow-400 via-green-400 via-blue-400 to-purple-400',
    borderClass: 'border-purple-500',
    isLegendary: true
  },
  {
    level: 10,
    name: '(神秘：未來解鎖)',
    description: '待訂',
    timeRequirement: 'LV9經過1年後可申請升等',
    requirements: ['成為企業共同創辦人', '承擔企業發展責任', '引領行業變革', '實現社會價值創造', '建立永續經營模式'],
    benefits: ['全方位福利保障', '股權分紅', '決策參與權', '自主創業支援', '個人品牌建設支援'],
    hourlyRate: 500,
    colorClass: 'bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-400',
    gradientClass: 'from-yellow-300 to-amber-400',
    borderClass: 'border-amber-500',
    isPartner: true
  },
  {
    level: 11,
    name: '(待訂)',
    description: '內容待訂，請至後台設定',
    timeRequirement: 'LV10經過1年後可申請升等',
    requirements: [],
    benefits: [],
    hourlyRate: 500,
    colorClass: 'bg-slate-500',
    gradientClass: 'from-slate-400 to-slate-600',
    borderClass: 'border-slate-600',
    isPartner: true
  },
  {
    level: 12,
    name: '(待訂)',
    description: '內容待訂，請至後台設定',
    timeRequirement: 'LV11經過1年後可申請升等',
    requirements: [],
    benefits: [],
    hourlyRate: 500,
    colorClass: 'bg-slate-500',
    gradientClass: 'from-slate-400 to-slate-600',
    borderClass: 'border-slate-600',
    isPartner: true
  }
]

/** 出廠地圖的最高職等 */
export const FALLBACK_MAX_LEVEL = 12

/*
 * 原本這裡有 getLevelConfig / getLevelName / getLevelColorClass 三個工具函式。
 * 職等資料改由夥伴地圖（LevelMapContext / levelMapService）提供後，三者已無任何呼叫端，
 * 留著只會讓人以為還能用它們查職等——而它們回的是出廠值，不分部門。
 * 本檔現在唯一的用途是 LevelMapContext 的出廠回退資料（levelData 陣列）。
 */
